import PrivateRoute from 'components/common/PrivateRoute';
import { Navigate, createBrowserRouter } from 'react-router-dom';
import Root from 'App/Root';
import { NewsPageOutlet, Users as NewsUsers } from 'pages/NewsPage';
import { Users as EnAppSysUsers, EnappsysOutlet } from 'pages/EnAppSys';
import { AlertsOutlet, Users as AlertsUsers } from 'pages/Alerts';
import {
	EnergyQuantifiedOutlet,
	Users as EnergyQuantifiedUsers,
} from 'pages/EnergyQuantified';
import { AdminAccountsOutlet } from 'pages/Account/AdminAccounts';
import { AdminAccountsPage } from 'pages/Account/AdminAccounts/AdminAccountsPage';
import { AdminAccountProfilePage } from 'pages/Account/AdminAccounts/AdminAccountProfilePage';
import AccountsPage from 'pages/Account/Accounts/AccountsPage';
import AccountProfilePage from 'pages/Account/Accounts/AccountProfilePage';
import AccountsOutlet from 'pages/Account';
import { AccountsUsersOutlet } from 'pages/Account/Accounts';
import { MarketsOutlet, Users as MarketsUsers } from 'pages/Markets';
import DeveloperOutlet from 'pages/Developer/DeveloperOutlet';
import HubSpotSyncTool from 'pages/Developer/HubSpotSyncTool';
import { AccessControl } from 'components/common/AccessControl/AccessControl';
import { AdminRole } from 'domain/account/enums';
import FusionAuthSyncTool from 'pages/Developer/FusionAuthSyncTool';
import SubscriptionOutlet from 'pages/Subscription/SubscriptionOutlet';
import SubscriptionPage from 'pages/Subscription/SubscriptionPage';
import CreateSubscriptionPage from 'pages/Subscription/CreateSubscriptionPage';
import SubscriptionFormContextProvider from 'pages/Subscription/SubscriptionForm/SubscriptionFormContext';
import EditSubscriptionPage from 'pages/Subscription/EditSubscriptionPage';
import AssignSeatTool from 'pages/Developer/AssignSeatTool/AssignSeatTool';
import CompanyProfile from 'pages/Companies/CompanyProfile';
import CompaniesOutlet from 'pages/Companies/CompaniesOutlet';
import CompanyTable from 'pages/Companies/CompanyTable';
import {
	CreateSystemEntityPage,
	SystemEntities,
	SystemEntitiesList,
	SystemEntityProfilePage,
} from 'pages/SystemEntities';
import YouniumAccountImportTool from 'pages/Developer/YouniumAccountImportTool';
import { PermissionCacheTools } from 'pages/Developer/PermissionCacheTools/PermissionCacheTools';
import { SysPowerOutlet, Users as SysPowerUsers } from 'pages/SysPower';
import {
	DataFabricatorOutlet,
	Users as DataFabricatorUsers,
} from 'pages/DataFabricator';
import { MontelPPAOutlet, Users as MontelPPAUsers } from 'pages/MontelPPA';
import UnusedSubscriptionsTool from 'pages/Developer/UnusedSubscriptionsTool/UnusedSubscriptionsTool';
import PermissionsOverview from 'pages/Developer/PermissionsOverview';
import {
	FlexInsightOutlet,
	Users as FlexInsightUsers,
} from 'pages/FlexInsight';
import ToolsOutlet from 'pages/Tools/ToolsOutlet';
import { ProviderBlacklist } from 'pages/Markets/ProviderBlacklist/ProviderBlacklist';
import { DefaultProviderBlacklist } from 'pages/Markets/DefaultProviderBlacklist/DefaultProviderBlacklist';
import SyncAdminAccountsTool from 'pages/Developer/SyncAdminAccountsTool';
import MatchUsers from 'pages/Marketplace/Users';
import { ApplicationSubscriptionsTable } from 'components/subscription/ApplicationSubscriptionTable/ApplicationSubscriptionsTable';
import { ProductName } from 'domain/product/enums/ProductName';
import { getProductConfigByName } from 'domain/product/productMap';
import CreateBundlePage from 'pages/SubscriptionBundle/CreateBundlePage';
import BundlePage from 'pages/SubscriptionBundle/BundlePage';
import BundleOutlet from 'pages/SubscriptionBundle/BundleOutlet';
import BundleFormContextProvider from 'pages/SubscriptionBundle/BundleForm/BundleFormContext';
import EditBundlePage from 'pages/SubscriptionBundle/EditBundlePage';
import TrackingStateGenerator from 'pages/Tools/TrackingStateGenerator';
import ProviderAccessExportPage from 'pages/Markets/ProviderAccessExport';
import PrimaryAccountToolPage from 'pages/Tools/PrimaryAccounts';

const router = createBrowserRouter([
	{
		path: '/',
		element: (
			<PrivateRoute>
				<Root />
			</PrivateRoute>
		),
		children: [
			{
				path: '',
				element: <Navigate to="/accounts/users" replace />,
			},
			{
				path: 'accounts',
				element: <AccountsOutlet />,
				children: [
					{
						path: '',
						element: <Navigate to="/accounts/users" replace />,
					},
					{
						path: 'users',
						element: <AccountsUsersOutlet />,
						children: [
							{
								path: '',
								element: <AccountsPage />,
							},
							{
								path: ':id',
								element: <AccountProfilePage />,
							},
						],
					},
					{
						path: 'admin-users',
						element: <AdminAccountsOutlet />,
						children: [
							{
								path: '',
								element: <AdminAccountsPage />,
							},
							{
								path: ':id',
								element: <AdminAccountProfilePage />,
							},
						],
					},
				],
			},
			{
				path: 'systementities',
				element: <SystemEntities />,
				children: [
					{
						path: '',
						element: <SystemEntitiesList />,
					},
					{
						path: 'create',
						element: <CreateSystemEntityPage />,
					},
					{
						path: ':id',
						element: <SystemEntityProfilePage />,
					},
				],
			},
			{
				path: 'newspage',
				element: (
					<PrivateRoute>
						<NewsPageOutlet />
					</PrivateRoute>
				),
				children: [
					{
						path: '',
						element: <Navigate to="/newspage/users" replace />,
					},
					{
						path: 'users',
						element: <NewsUsers />,
					},
					{
						path: 'subscriptions',
						element: (
							<ApplicationSubscriptionsTable
								id={
									getProductConfigByName(ProductName.NewsPage)
										?.id
								}
							/>
						),
					},
				],
			},
			{
				path: 'enappsys',
				element: (
					<PrivateRoute>
						<EnappsysOutlet />
					</PrivateRoute>
				),
				children: [
					{
						path: '',
						element: <Navigate to="/enappsys/users" replace />,
					},
					{
						path: 'users',
						element: <EnAppSysUsers />,
					},
					{
						path: 'subscriptions',
						element: (
							<ApplicationSubscriptionsTable
								id={
									getProductConfigByName(ProductName.EnAppSys)
										?.id
								}
							/>
						),
					},
				],
			},
			{
				path: 'flex-insight',
				element: (
					<PrivateRoute>
						<FlexInsightOutlet />
					</PrivateRoute>
				),
				children: [
					{
						path: '',
						element: <Navigate to="/flex-insight/users" replace />,
					},
					{
						path: 'users',
						element: <FlexInsightUsers />,
					},
					{
						path: 'subscriptions',
						element: (
							<ApplicationSubscriptionsTable
								id={
									getProductConfigByName(
										ProductName.FlexInsight,
									)?.id
								}
							/>
						),
					},
				],
			},
			{
				path: 'alerts',
				element: (
					<PrivateRoute>
						<AlertsOutlet />
					</PrivateRoute>
				),
				children: [
					{
						path: '',
						element: <Navigate to="/alerts/users" replace />,
					},
					{
						path: 'users',
						element: <AlertsUsers />,
					},
				],
			},
			{
				path: 'energy-quantified',
				element: (
					<PrivateRoute>
						<EnergyQuantifiedOutlet />
					</PrivateRoute>
				),
				children: [
					{
						path: '',
						element: (
							<Navigate to="/energy-quantified/users" replace />
						),
					},
					{
						path: 'users',
						element: <EnergyQuantifiedUsers />,
					},
					{
						path: 'subscriptions',
						element: (
							<ApplicationSubscriptionsTable
								id={
									getProductConfigByName(
										ProductName.EnergyQuantified,
									)?.id
								}
							/>
						),
					},
				],
			},
			{
				path: 'markets',
				element: (
					<PrivateRoute>
						<MarketsOutlet />
					</PrivateRoute>
				),
				children: [
					{
						path: '',
						element: <Navigate to="/markets/users" replace />,
					},
					{
						path: 'users',
						element: <MarketsUsers />,
					},
					{
						path: 'subscriptions',
						element: (
							<ApplicationSubscriptionsTable
								id={
									getProductConfigByName(
										ProductName.MontelMarkets,
									)?.id
								}
							/>
						),
					},
					{
						path: 'provider-blacklist',
						element: <ProviderBlacklist />,
					},
					{
						path: 'default-provider-blacklist',
						element: <DefaultProviderBlacklist />,
					},
					{
						path: 'provider-access-export',
						element: <ProviderAccessExportPage />,
					},
				],
			},
			{
				path: 'syspower',
				element: (
					<PrivateRoute>
						<SysPowerOutlet />
					</PrivateRoute>
				),
				children: [
					{
						path: '',
						element: <Navigate to="/syspower/users" replace />,
					},
					{
						path: 'users',
						element: <SysPowerUsers />,
					},
					{
						path: 'subscriptions',
						element: (
							<ApplicationSubscriptionsTable
								id={
									getProductConfigByName(ProductName.SysPower)
										?.id
								}
							/>
						),
					},
				],
			},
			{
				path: 'data-fabricator',
				element: (
					<PrivateRoute>
						<DataFabricatorOutlet />
					</PrivateRoute>
				),
				children: [
					{
						path: '',
						element: (
							<Navigate to="/data-fabricator/users" replace />
						),
					},
					{
						path: 'users',
						element: <DataFabricatorUsers />,
					},
					{
						path: 'subscriptions',
						element: (
							<ApplicationSubscriptionsTable
								id={
									getProductConfigByName(
										ProductName.DataFabricator,
									)?.id
								}
							/>
						),
					},
				],
			},
			{
				path: 'montel-ppa',
				element: (
					<PrivateRoute>
						<MontelPPAOutlet />
					</PrivateRoute>
				),
				children: [
					{
						path: '',
						element: <Navigate to="/montel-ppa/users" replace />,
					},
					{
						path: 'users',
						element: <MontelPPAUsers />,
					},
					{
						path: 'subscriptions',
						element: (
							<ApplicationSubscriptionsTable
								id={
									getProductConfigByName(
										ProductName.MontelPPA,
									)?.id
								}
							/>
						),
					},
				],
			},
			{
				path: 'developer',
				element: (
					<PrivateRoute>
						<AccessControl allowedRoles={[AdminRole.DEVELOPER]}>
							<DeveloperOutlet />
						</AccessControl>
					</PrivateRoute>
				),
				children: [
					{
						path: '',
						element: <>Collection of developer tools</>,
					},
					{
						path: 'hubspot',
						element: <HubSpotSyncTool />,
					},
					{
						path: 'fusionauth',
						element: <FusionAuthSyncTool />,
					},
					{
						path: 'assign-seat',
						element: <AssignSeatTool />,
					},
					{
						path: 'permissions-cache',
						element: <PermissionCacheTools />,
					},
					{
						path: 'younium-import',
						element: <YouniumAccountImportTool />,
					},
					{
						path: 'sync-admin-accounts',
						element: <SyncAdminAccountsTool />,
					},
				],
			},
			{
				path: 'tools',
				element: (
					<PrivateRoute>
						<ToolsOutlet />
					</PrivateRoute>
				),
				children: [
					{
						path: '',
						element: <>Collection of tools</>,
					},
					{
						path: 'permissions-overview',
						element: <PermissionsOverview />,
					},
					{
						path: 'unused-subscriptions',
						element: <UnusedSubscriptionsTool />,
					},
					{
						path: 'tracking-state-generator',
						element: <TrackingStateGenerator />,
					},
					{
						path: 'primary-accounts',
						element: <PrimaryAccountToolPage />,
					},
				],
			},
			{
				path: 'subscriptions',
				element: (
					<PrivateRoute>
						<SubscriptionFormContextProvider>
							<SubscriptionOutlet />
						</SubscriptionFormContextProvider>
					</PrivateRoute>
				),
				children: [
					{
						path: ':id',
						element: <SubscriptionPage />,
					},
					{
						path: ':id/edit',
						element: <EditSubscriptionPage />,
					},
					{
						path: 'create',
						element: <CreateSubscriptionPage />,
					},
				],
			},
			{
				path: 'subscription-bundles',
				element: (
					<PrivateRoute>
						<BundleFormContextProvider>
							<BundleOutlet />
						</BundleFormContextProvider>
					</PrivateRoute>
				),
				children: [
					{
						path: 'create',
						element: <CreateBundlePage />,
					},
					{
						path: ':id/edit',
						element: <EditBundlePage />,
					},
					{
						path: ':id',
						element: <BundlePage />,
					},
				],
			},
			{
				path: 'companies',
				element: (
					<PrivateRoute>
						<CompaniesOutlet />
					</PrivateRoute>
				),
				children: [
					{
						path: '',
						element: <CompanyTable />,
					},
					{
						path: ':id',
						element: <CompanyProfile />,
					},
				],
			},
		],
	},
	{
		path: '*',
		element: (
			<PrivateRoute>
				<div>404</div>
			</PrivateRoute>
		),
	},
]);

export default router;
